import { useEffect, useState } from "react";
import { Loader2, User } from "lucide-react";
import { Card } from "@/components/ui/card";
import { CustomerSourceBadge } from "@/features/customer/components/customer-source-badge";
import { useSearchCustomerByPhone } from "@/features/customer/api";
import type { Customer } from "@/types/customer";

// VN-mobile pattern. The lookup hook re-checks this; we duplicate here so
// the UI can decide whether to show "loading" or stay idle while the
// number is still incomplete.
const VN_PHONE_REGEX = /^(\+84|0)\d{9,10}$/;

export type CustomerLookupState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "match"; customer: Customer }
  | { kind: "no-match" };

export interface CustomerPhoneLookupProps {
  /** Raw phone string from the input (whitespace tolerated). */
  phone: string;
  /** Fires every time the resolved state changes. Parent uses this to
   *  pin `customerState` for the submit handler. */
  onStateChange: (state: CustomerLookupState) => void;
}

/**
 * Debounced phone-lookup widget. Owns the 300ms debounce, the gated
 * `useSearchCustomerByPhone` call, and the visual feedback (spinner,
 * match-card). Does NOT render the phone input itself — that lives on
 * the parent form so RHF can validate it.
 */
export function CustomerPhoneLookup({
  phone,
  onStateChange,
}: CustomerPhoneLookupProps) {
  const [debouncedPhone, setDebouncedPhone] = useState<string>("");

  useEffect(() => {
    const trimmed = phone.replace(/\s+/g, "");
    const handle = window.setTimeout(() => setDebouncedPhone(trimmed), 300);
    return () => window.clearTimeout(handle);
  }, [phone]);

  const query = useSearchCustomerByPhone(debouncedPhone);

  // Resolve the four states. Mid-typing (phone hasn't matched the regex
  // yet) we sit idle so the user doesn't see spinner flicker on every
  // keystroke. Once the regex is satisfied AND the debounce has settled,
  // the query fires.
  const phoneIsValid =
    debouncedPhone.length > 0 && VN_PHONE_REGEX.test(debouncedPhone);

  let state: CustomerLookupState;
  if (!phoneIsValid) {
    state = { kind: "idle" };
  } else if (query.isFetching) {
    state = { kind: "loading" };
  } else if (query.data !== undefined && query.data !== null) {
    state = { kind: "match", customer: query.data };
  } else if (query.isSuccess && query.data === null) {
    state = { kind: "no-match" };
  } else {
    // Brief gap before the first fetch starts.
    state = { kind: "loading" };
  }

  // Notify the parent on every state transition. We compare via a
  // stringified key so we don't fire on identity changes alone.
  const stateKey =
    state.kind === "match" ? `match-${state.customer.id}` : state.kind;
  useEffect(() => {
    onStateChange(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey, onStateChange]);

  if (state.kind === "idle") return null;

  if (state.kind === "loading") {
    return (
      <p
        role="status"
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <Loader2 aria-hidden="true" className="size-4 animate-spin" />
        Đang tìm khách...
      </p>
    );
  }

  if (state.kind === "match") {
    return <ExistingCustomerCard customer={state.customer} />;
  }

  // no-match — let the parent reveal its editable customer fields. We
  // render a small hint banner above them.
  return (
    <p className="text-xs italic text-muted-foreground">
      Khách hàng mới — vui lòng điền thông tin
    </p>
  );
}

interface ExistingCustomerCardProps {
  customer: Customer;
}

function ExistingCustomerCard({ customer }: ExistingCustomerCardProps) {
  return (
    <Card
      role="status"
      aria-label="Khách hàng đã có"
      className="flex flex-col gap-2 border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/40"
    >
      <div className="flex items-start gap-2">
        <User
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-emerald-700 dark:text-emerald-300"
        />
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              Khách hàng đã có
            </p>
            <CustomerSourceBadge source={customer.source} />
          </div>
          <p className="text-sm font-semibold leading-tight">
            {customer.fullName}
          </p>
          {customer.email ? (
            <p className="text-xs text-muted-foreground">{customer.email}</p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Số điện thoại đã tồn tại trong hệ thống — sẽ liên kết lead mới với
            khách hàng này.
          </p>
        </div>
      </div>
    </Card>
  );
}
